import axios from "axios";
import Cookies from "js-cookie";
import { HOST_NAME } from "../utils/constants";

export const createPage = async (
  data: any,
  thumbnails: File[],
  internalImages: File[]
) => {
  return new Promise((resolve, reject) => {
    // Configure the request
    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/page`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    // Send the request
    axios
      .request(config)
      .then(async (response) => {
        await uploadThumbnailAndInternalImagesToS3(
          response?.data?.data?._id,
          thumbnails[0],
          internalImages
        );
        resolve(response?.data?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getInternalImages = async (
  pageId: string,
  modifiedInternalImages: Array<string | File> // More precise typing
): Promise<string[]> => {
  const page: any = await getPageById(pageId);
  const originalInternalImages: string[] = page?.internalImages || [];
  const deletedImagesUrls: string[] = [];
  const finalImages: any = [...modifiedInternalImages]; // Create a copy to modify

  // 1. Upload new files and replace them with URLs
  for (let i = 0; i < finalImages.length; i++) {
    const image = finalImages[i];

    if (image instanceof File || typeof image !== "string") {
      try {
        const uploadResponse: any = await uploadFileToS3(
          image,
          { pageId },
          "page"
        );
        finalImages[i] = uploadResponse.signedUrl.split("?")[0];
      } catch (err) {
        console.error(`Error uploading image at index ${i}:`, err);
        // Keep the original value if upload fails
        finalImages[i] = originalInternalImages[i] || "";
      }
    }
  }

  // 2. Find images to delete (present in original but not in final)
  const imagesToDelete = originalInternalImages.filter(
    (originalUrl) =>
      originalUrl && // Ensure URL exists
      !finalImages.includes(originalUrl) && // Not in final array
      !deletedImagesUrls.includes(originalUrl) // Avoid duplicates
  );

  // 3. Delete unused images (fire and forget, don't await)
  if (imagesToDelete.length > 0) {
    deleteDocumentsByUrl(imagesToDelete).catch((err) => {
      console.error("Error deleting images:", err);
    });
  }

  return finalImages;
};

export const updatePage = async (
  id: string,
  data: any,
  thumbnailFiles?: File[]
) => {
  const internalImages: Array<string> = await getInternalImages(
    id,
    data?.internalImages || []
  ); // In this function we are checking the difference between original internalImages and modified internalImages

  data["internalImages"] = internalImages;
  return new Promise((resolve, reject) => {
    const config = {
      method: "patch",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/page/${id}`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then(async (response) => {
        if (thumbnailFiles && thumbnailFiles?.length > 0) {
          await uploadThumbnailAndInternalImagesToS3(
            response?.data?.data?._id,
            thumbnailFiles[0],
            []
          );
        }
        resolve(response?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const uploadThumbnailAndInternalImagesToS3 = async (
  pageId: string,
  thumbnail: File,
  internalImagesFiles: File[]
) => {
  let thumbnailUrl: string = "";
  let internalImages: Array<String> = [];

  //Upload Thumbnail and get the url
  if (thumbnail) {
    await uploadFileToS3(thumbnail, { pageId }, "page")
      .then((res: any) => (thumbnailUrl = res.signedUrl.split("?")[0]))
      .catch((err) => console.log("error uploading thumbnail"));

    //Update the Page i.e add the thumbnailUrl
    await updatePage(pageId, { thumbnailUrl })
      .then((res) => {
        console.log("ThumbnailUrl added successfully.");
      })
      .catch((err) => "error adding thumbnailUrl.");
  }

  //Upload internal Images and get the url
  if (internalImagesFiles.length > 0) {
    try {
      const uploadedImages = await Promise.all(
        internalImagesFiles.map(async (file) => {
          try {
            const res: any = await uploadFileToS3(file, { pageId }, "page");
            return res.signedUrl.split("?")[0]; // Extract URL before query params
          } catch (err) {
            console.error("Error uploading Internal Image.", err);
            return null; // Preserve array order by returning null for failed uploads
          }
        })
      );

      // Filter out any failed uploads (optional, based on requirements)
      internalImages = uploadedImages.filter((url) => url !== null);
    } catch (error) {
      console.error("Error uploading images:", error);
    }

    //Update the Page i.e add the thumbnailUrl
    await updatePage(pageId, { internalImages })
      .then((res) => {
        console.log("Internal Images added successfully.");
      })
      .catch((err) => "error adding Internal Images.");
  }
};

export const uploadFileToS3 = (
  file: File,
  metadata: any,
  folderName: string
) => {
  return new Promise((resolve, reject) => {
    // Step 1: Get pre-signed URL
    const configPresignedUrl = {
      method: "post",
      url: `${HOST_NAME}/generate-presigned-url`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
        "Content-Type": "application/json",
      },
      data: {
        fileName: file.name,
        fileType: file.type,
        metadata,
        folderName,
      },
    };

    axios
      .request(configPresignedUrl)
      .then(async (response) => {
        const { signedUrl, key } = response?.data?.data;

        // Step 2: Upload file to S3
        const configUploadToS3 = {
          method: "put",
          url: signedUrl,
          headers: {
            "Content-Type": file.type,
          },
          data: file,
        };

        const res = await axios
          .request(configUploadToS3)
          .then(() => ({ signedUrl, key }));

        return res;
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        reject(error);
      });
  });
};

export const uploadDocument = async (file: any) => {
  let formData = new FormData();
  formData.append("files", file);
  try {
    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/documents/upload`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
        "Content-Type": "multipart/form-data",
      },
      data: formData,
    };

    const response = await axios.request(config);
    return response?.data;
  } catch (error) {
    return error;
  }
};

export const getAllPages = (filters?: any) => {
  let status: string = "";
  if (filters?.status) {
    status = filters?.status;
  }

  return new Promise((resolve, reject) => {
    const categoriesArray = filters?.categories || [];
    const collectionsArray = filters?.collections || [];
    const licensesArray = filters?.licenses || [];

    const categoriesJSON =
      categoriesArray?.length !== 0
        ? `${categoriesArray?.map((element: any) => `"${element}"`).join(" ")}`
        : "";

    const collectionsJSON =
      collectionsArray?.length !== 0
        ? `${collectionsArray?.map((element: any) => `"${element}"`).join(" ")}`
        : "";

    const licensesJSON =
      licensesArray?.length !== 0
        ? `${licensesArray?.map((element: any) => `"${element}"`).join(" ")}`
        : "";

    const config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/page?search=${filters?.searchBy || ""}&searchByTitle=${
        filters?.searchByTitle || ""
      }&page=${filters?.page || 1}&pageSize=${
        filters?.pageSize || 25
      }&category=${categoriesJSON}&collection=${collectionsJSON}&license=${licensesJSON}&status=${status}`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getPageById = (id: string) => {
  return new Promise((resolve, reject) => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/page/${id}`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getPageSourceCodeByPageId = (pageId: string) => {
  return new Promise((resolve, reject) => {
    const config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/page-source/${pageId}`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const deletePage = (id: string) => {
  return new Promise((resolve, reject) => {
    const config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/page/${id}`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const deleteDocumentsByUrl = (data: any) => {
  return new Promise((resolve, reject) => {
    const config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/documents/url`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
      data: { urls: data },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const deleteDocuments = (data: any) => {
  return new Promise((resolve, reject) => {
    const config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/documents`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
      data: { documentIds: data },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const updateMultiplePages = (data: any) => {
  return new Promise((resolve, reject) => {
    const config = {
      method: "patch",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/multiple-page`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getAllPagesFeedback = (filters?: any) => {
  return new Promise<any>((resolve, reject) => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/feedbacked-page?search=${
        filters?.searchBy || ""
      }&page=${filters?.page || 1}&pageSize=${filters?.pageSize || 25}`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error) => {
        console.log(error);
        reject(error);
      });
  });
};

// export const getAllParticularPageFeedbacks = (filters?: any) => {
//   return new Promise((resolve, reject) => {
//     const page =
//       filters?.page !== undefined &&
//       filters?.page !== null &&
//       filters?.page !== ""
//         ? filters?.page
//         : "";
//     const pageSize =
//       filters?.pageSize !== undefined &&
//       filters?.pageSize !== null &&
//       filters?.pageSize !== ""
//         ? filters?.pageSize
//         : "";
//     const pageId =
//       filters?.pageId !== undefined &&
//       filters?.pageId !== null &&
//       filters?.pageId !== ""
//         ? filters?.pageId
//         : "";

//     let config = {
//       method: "get",
//       maxBodyLength: Infinity,
//       url: `${HOST_NAME}/feedback?pageId=${pageId}&page=${page}&pageSize=${pageSize}`,
//       headers: {
//         Authorization: `Bearer ${Cookies.get("jstoken")}`,
//       },
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         resolve(response.data.data);
//       })
//       .catch((error) => {
//         reject(error);
//       });
//   });
// };

export const updateFeedbackById = (data: any, id: string) => {
  return new Promise((resolve, reject) => {
    let config = {
      method: "patch",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/feedback/${id}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data);
      })
      .catch((error) => {
        console.log(error);
        reject(error);
      });
  });
};

export const getAllPagesReport = (filters?: any) => {
  return new Promise<any>((resolve, reject) => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/reported-page?search=${
        filters?.searchBy || ""
      }&page=${filters?.page || 1}&pageSize=${filters?.pageSize || 25}`,
      headers: {
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error) => {
        console.log(error);
        reject(error);
      });
  });
};

// export const getAllReports = (filters?: any) => {
//   return new Promise((resolve, reject) => {
//     const page =
//       filters?.page !== undefined &&
//       filters?.page !== null &&
//       filters?.page !== ""
//         ? filters?.page
//         : "";
//     const pageSize =
//       filters?.pageSize !== undefined &&
//       filters?.pageSize !== null &&
//       filters?.pageSize !== ""
//         ? filters?.pageSize
//         : "";
//     const pageId =
//       filters?.pageId !== undefined &&
//       filters?.pageId !== null &&
//       filters?.pageId !== ""
//         ? filters?.pageId
//         : "";

//     let config = {
//       method: "get",
//       maxBodyLength: Infinity,
//       url: `${HOST_NAME}/problem?pageId=${pageId}&page=${page}&pageSize=${pageSize}`,
//       headers: {
//         Authorization: `Bearer ${Cookies.get("jstoken")}`,
//       },
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         resolve(response?.data?.data);
//       })
//       .catch((error) => {
//         reject(error);
//       });
//   });
// };

export const updateReportById = (data: any, id: string) => {
  return new Promise((resolve, reject) => {
    let config = {
      method: "patch",
      maxBodyLength: Infinity,
      url: `${HOST_NAME}/problem/${id}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${Cookies.get("jstoken")}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        resolve(response?.data);
      })
      .catch((error) => {
        console.log(error);
        reject(error);
      });
  });
};

// export const getAllPagesCollections = (filters?: any) => {
//   return new Promise<any>((resolve, reject) => {
//     let config = {
//       method: "get",
//       maxBodyLength: Infinity,
//       url: `${HOST_NAME}/collection?search=${filters?.searchBy || ""}&page=${
//         filters?.page || 1
//       }&pageSize=${filters?.pageSize || 25}&includePages=${
//         filters?.includePages || ""
//       }&type=${filters?.type || ""}`,
//       headers: {
//         Authorization: `Bearer ${Cookies.get("jstoken")}`,
//         "Content-Type": "application/json",
//       },
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         resolve(response?.data?.data);
//       })
//       .catch((error) => {
//         console.log(error);
//         reject(error);
//       });
//   });
// };

// export const createPageCollection = (data: any) => {
//   return new Promise<any>((resolve, reject) => {
//     let config = {
//       method: "post",
//       maxBodyLength: Infinity,
//       url: `${HOST_NAME}/collection`,
//       headers: {
//         Authorization: `Bearer ${Cookies.get("jstoken")}`,
//         "Content-Type": "application/json",
//       },
//       data: data,
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         resolve(response?.data?.data);
//       })
//       .catch((error) => {
//         console.log(error);
//         reject(error);
//       });
//   });
// };

// export const deletePageCollection = (id: string) => {
//   return new Promise<void>((resolve, reject) => {
//     let config = {
//       method: "delete",
//       maxBodyLength: Infinity,
//       url: `${HOST_NAME}/collection/${id}`,
//       headers: {
//         Authorization: `Bearer ${Cookies.get("jstoken")}`,
//       },
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         resolve(response?.data?.data);
//       })
//       .catch((error) => {
//         console.log(error);
//         reject(error);
//       });
//   });
// };

// export const updatePageCollection = (id: any, data: any) => {
//   return new Promise<any>((resolve, reject) => {
//     let config = {
//       method: "patch",
//       maxBodyLength: Infinity,
//       url: `${HOST_NAME}/collection/${id}`,
//       headers: {
//         Authorization: `Bearer ${Cookies.get("jstoken")}`,
//         "Content-Type": "application/json",
//       },
//       data: data,
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         resolve(response?.data?.data);
//       })
//       .catch((error) => {
//         console.log(error);
//         reject(error);
//       });
//   });
// };
